import { DisjointSetForest } from "./disjoint-sets";
import type {
    BoundingBox,
    LinesForBoundingBox} from "./bounding-box";
import {
    computeBoundingBox,
    directionAngleForBoundingBox,
    linesForBoundingBox
} from "./bounding-box";
import type { AngleCluster} from "./locator/angle-clustering";
import { lineLengthOnVectorSpace } from "./locator/angle-clustering";
import { PI_2 } from "./constants";
import type {
    Quadrilateral} from "common/geometry";
import {
    areaOfConvexQuadrilateral,
    areaOfConvexQuadrilateralOverlap,
    effectiveIntersectionZero
} from "common/geometry";
import type { MomentsVectorSpace } from "./locator/moments";

export class ChunkMatrix {
    private readonly buffer: (AngleCluster | undefined)[];

    constructor(
        public readonly width: number,
        public readonly height: number,
        public readonly xStep: number,
        public readonly yStep: number
    ) {
        this.buffer = new Array(width * height);
    }

    public reset(): ChunkMatrix {
        for (let y = 0; y < this.height; y++) {
            const yOffset = y * this.width;
            for (let x = 0; x < this.width; x++) {
                this.buffer[yOffset + x] = undefined;
            }
        }
        return this;
    }

    public get(x: number, y: number): AngleCluster | undefined {
        return this.buffer[y * this.width + x];
    }

    public set(x: number, y: number, value: AngleCluster | undefined): AngleCluster | undefined {
        this.buffer[y * this.width + x] = value;
        return value;
    }
}

export type ClusterChunksParameters = Readonly<{
    zeroEpsilon: number;
    angleEpsilon: number;
    minFactor: number;
    maxFactor: number;
    minClusterSize: number;
    distanceSinAllowance: number;
}>;

function minDistanceForUnifying(cluster: AngleCluster): number {
    if (cluster.vectorSpaces.length === 1) {
        // This is the thickness of the single component in the cluster.
        return cluster.vectorSpaces[0].minorLength;
    } else {
        return cluster.distanceStatistics.min;
    }
}

function maxDistanceForUnifying(cluster: AngleCluster): number {
    if (cluster.vectorSpaces.length === 1) {
        // This is the thickness of the single component in the cluster.
        return cluster.vectorSpaces[0].minorLength;
    } else {
        return cluster.distanceStatistics.max;
    }
}

function vectorSpaceDistance(
    vs1: MomentsVectorSpace,
    vs2: MomentsVectorSpace,
    xOffset: number,
    yOffset: number
): number {
    const dxCenter = vs2.centerX + xOffset - vs1.centerX;
    const dyCenter = vs2.centerY + yOffset - vs1.centerY;
    const theta = Math.atan(dyCenter / dxCenter);
    const hyp = Math.sqrt(dxCenter * dxCenter + dyCenter * dyCenter);

    const intersect1 = lineLengthOnVectorSpace(vs1, theta);
    const intersect2 = lineLengthOnVectorSpace(vs2, theta);

    return Math.max(hyp - intersect1 - intersect2, 0);
}

function clustersAreCloseEnough(
    cur: AngleCluster,
    prior: AngleCluster,
    curX: number,
    curY: number,
    { minFactor, maxFactor, distanceSinAllowance }: ClusterChunksParameters,
    acceptDistances: Map<AngleCluster, number>
): boolean {
    let dx: number,
        dy: number,
        interAngle: number,
        distance = Number.MAX_SAFE_INTEGER;
    if (curY > 0) {
        if (curX > 0) {
            dx = cur.minX + curX - prior.maxX;
            dy = cur.minY + curY - prior.maxY;
        } else if (curX === 0) {
            dx = 0;
            dy = cur.minY + curY - prior.maxY;
        } else {
            dx = cur.maxX + curX - prior.minY;
            dy = cur.minY + curY - prior.maxY;
        }
        distance = Math.sqrt(dx * dx + dy * dy);
        interAngle = Math.acos(dx / distance);
    } else {
        // We assume that curY === 0 and curX > 0 here.
        distance = cur.minX + curX - prior.maxX;
        interAngle = 0;
    }
    // Consider two chunks, each with an angle of 90 degrees or so.
    // Merge eligibility is pretty good for these two chunks if they are next
    // to each other on the Y axis. However, if one is on top of another, we
    // don't want to be so open to merging them, because they might very well
    // be another barcode entirely.
    //
    // We consider the relative angle between these two chunks the "inter angle".
    // And we want to scale our tolerance for distance according to the
    // difference between this "inter angle" and the expected angle of the prior,
    // under the assumption that our current angle will more or less be the same.
    //
    // Because we are considering the difference, the resulting scaling factor
    // is actually the Sine difference between the angles.
    const interSin = Math.sin(Math.abs(interAngle - prior.theta));
    const sinMinFactor =
        minFactor <= distanceSinAllowance
            ? minFactor
            : distanceSinAllowance + interSin * (minFactor - distanceSinAllowance);
    const sinMaxFactor =
        maxFactor <= distanceSinAllowance
            ? maxFactor
            : distanceSinAllowance + interSin * (maxFactor - distanceSinAllowance);
    const threshMinDistance = sinMinFactor * Math.min(minDistanceForUnifying(cur), minDistanceForUnifying(prior));
    const threshMaxDistance = sinMaxFactor * Math.max(maxDistanceForUnifying(cur), maxDistanceForUnifying(prior));
    if (distance > threshMinDistance && distance > threshMaxDistance) {
        const priorAcceptDistance = acceptDistances.get(prior);
        if (priorAcceptDistance === undefined || priorAcceptDistance < distance) {
            return false;
        }
    }
    // We've passed the distance checks for bounding boxes, now let's consider
    // distance based on centers in the angle cluster.
    acceptDistances.set(cur, distance);
    return true;
}

interface ProposedChunkClusterAngle {
    totalRadians: number;
    totalCount: number;
}

function angleForCluster(proposed: ProposedChunkClusterAngle) {
    return proposed.totalRadians / proposed.totalCount;
}

function clusterAngleDistance(
    clusters: Map<number, ProposedChunkClusterAngle>,
    leftID: number,
    rightID: number,
    zeroEpsilon: number
): number {
    const leftCluster = clusters.get(leftID);
    const rightCluster = clusters.get(rightID);
    if (leftCluster === undefined || rightCluster === undefined) {
        return Number.MAX_SAFE_INTEGER;
    }

    let leftAngle = angleForCluster(leftCluster);
    leftAngle = leftAngle > Math.PI - zeroEpsilon ? 0 : leftAngle;

    let rightAngle = angleForCluster(rightCluster);
    rightAngle = rightAngle > Math.PI - zeroEpsilon ? 0 : rightAngle;

    return Math.abs(leftAngle - rightAngle);
}

function mergeClusterChunks(
    replacements: DisjointSetForest,
    clusters: Map<number, ProposedChunkClusterAngle>,
    dstID: number,
    srcID: number,
    zeroEpsilon: number
) {
    const srcReplacement = replacements.find(srcID);
    const firstDstReplacement = replacements.find(dstID);
    if (srcReplacement === undefined || firstDstReplacement === undefined) return;

    const src = clusters.get(srcReplacement);
    const dst = clusters.get(firstDstReplacement);
    if (src === undefined || dst === undefined || Object.is(src, dst)) return;

    const dstReplacement = replacements.union(srcID, dstID);

    if (dstReplacement !== srcID) {
        clusters.delete(srcID);
    }

    // We've been replaced in the disjoint set with some other identifier,
    // so we need to move the proposed angle record accordingly.
    if (dstReplacement !== undefined && dstReplacement !== dstID) {
        clusters.set(dstReplacement, dst);
        clusters.delete(dstID);
    }

    const srcAngle = angleForCluster(src);
    const dstAngle = angleForCluster(dst);

    const dstZero = dstAngle > Math.PI - zeroEpsilon;
    const srcZero = srcAngle > Math.PI - zeroEpsilon;

    let updated = dstZero ? 0 : dst.totalRadians;

    if (dstZero === srcZero) {
        updated += srcZero ? 0 : src.totalRadians;
    } else if (dstZero) {
        updated = src.totalRadians + (srcAngle <= PI_2 ? 0 : Math.PI * dst.totalCount);
    } else {
        // !dstZero, srcZero
        updated = dst.totalRadians + (dstAngle <= PI_2 ? 0 : Math.PI * src.totalCount);
    }

    dst.totalCount += src.totalCount;
    dst.totalRadians = updated;
}

export interface PossibleBarcode {
    boundingBox: BoundingBox;
    boundingLines: LinesForBoundingBox;
    chunks: readonly BoundingBox[];
}

type FoundChunk = Readonly<{
    angleCluster: AngleCluster;
    xOffset: number;
    yOffset: number;
}>;

export function clusterChunks(mat: ChunkMatrix, params: ClusterChunksParameters): PossibleBarcode[] {
    let nextLabel = 1;
    const labelReplacements = new DisjointSetForest();
    const clusters = new Map<number, ProposedChunkClusterAngle>();
    const { zeroEpsilon, angleEpsilon, minClusterSize, maxFactor } = params;
    const foundChunks: FoundChunk[] = [];
    const acceptDistances = new Map<AngleCluster, number>();

    function addProposedAngle(c: AngleCluster, x: number, y: number) {
        // Single-line "clusters" are special: we don't care about them
        // unless one of their neighbors (in any direction) has more than
        // one line. Multiple chunks with only one line in any expected
        // direction are hopefully rare.
        if (c.lineCount < 2) {
            let keepCluster = false;
            const cAngle = c.theta > Math.PI - zeroEpsilon ? 0 : c.theta;
            const cSpace = c.vectorSpaces[0];
            const maxDistance = cSpace.minorLength * maxFactor;

            for (let yN = y - 1; yN <= y + 1; yN++) {
                if (yN < 0 || mat.height <= yN) continue;

                for (let xN = x - 1; xN <= x + 1; xN++) {
                    if (xN < 0 || mat.width <= xN) continue;

                    const neighbor = mat.get(xN, yN);
                    if (neighbor === undefined) continue;
                    if (neighbor.lineCount <= 1) continue;

                    const neighborDistance = neighbor.distanceStatistics.average * maxFactor;

                    const nAngle = neighbor.theta > Math.PI - zeroEpsilon ? 0 : neighbor.theta;
                    if (Math.abs(cAngle - nAngle) > angleEpsilon) continue;

                    const nSpaces = neighbor.vectorSpaces;
                    for (let i = 0; i < nSpaces.length; i++) {
                        const vecDistance = vectorSpaceDistance(
                            cSpace,
                            nSpaces[i],
                            (xN - x) * mat.xStep,
                            (yN - y) * mat.yStep
                        );
                        if (vecDistance > maxDistance && vecDistance > neighborDistance) {
                            continue;
                        }

                        keepCluster = true;
                        break;
                    }

                    if (keepCluster) break;
                }
            }
            if (!keepCluster) return;
        }
        c.label = nextLabel;
        clusters.set(nextLabel, {
            totalRadians: c.theta,
            totalCount: 1,
        });
        labelReplacements.add(nextLabel);
        nextLabel++;
        foundChunks.push({
            angleCluster: c,
            xOffset: x * mat.xStep,
            yOffset: y * mat.yStep,
        });
    }

    // Top left cell needs extra special handling: there's nothing before it.
    let current = mat.get(0, 0);
    if (current !== undefined) {
        addProposedAngle(current, 0, 0);
    }

    // Top row needs special handling: there's nothing above it.
    for (let x = 1; x < mat.width; x++) {
        current = mat.get(x, 0);
        if (current === undefined) continue;

        addProposedAngle(current, x, 0);
        const prior = mat.get(x - 1, 0);
        if (
            prior !== undefined &&
            clustersAreCloseEnough(current, prior, mat.xStep, 0, params, acceptDistances) &&
            clusterAngleDistance(clusters, current.label, prior.label, zeroEpsilon) <= angleEpsilon
        ) {
            mergeClusterChunks(labelReplacements, clusters, prior.label, current.label, zeroEpsilon);
        }
    }

    // The rest of the rows can work normally.
    for (let y = 1; y < mat.height; y++) {
        // Leftmost cell needs special handling: there's only
        // something above it.
        current = mat.get(0, y);
        if (current !== undefined) {
            addProposedAngle(current, 0, y);

            let bestLabel: number | undefined;
            let bestDistance = Number.MAX_SAFE_INTEGER;
            for (let xP = 0; xP < 2; xP++) {
                const prior = mat.get(xP, y - 1);
                if (
                    prior !== undefined &&
                    clustersAreCloseEnough(current, prior, 0, mat.yStep, params, acceptDistances)
                ) {
                    const distance = clusterAngleDistance(clusters, current.label, prior.label, zeroEpsilon);
                    if (distance <= angleEpsilon && distance < bestDistance) {
                        bestLabel = prior.label;
                        bestDistance = distance;
                    }
                }
            }

            if (bestLabel !== undefined) {
                mergeClusterChunks(labelReplacements, clusters, bestLabel, current.label, zeroEpsilon);
            }
        }

        // Previously, we have been operating under the assumption that we don't
        // need to perform any set union operations. This assumption holds for the
        // entire first row: either the cells are adjacent, or they aren't; and
        // for the first column of every row: either the cells above are adjacent,
        // or they aren't. This is no longer generally true for the rest of the
        // image, so we'll need to perform repeated unions across all the labels
        // we find here.
        for (let x = 1; x < mat.width; x++) {
            current = mat.get(x, y);
            if (current === undefined) continue;

            addProposedAngle(current, x, y);

            const priors: [number, AngleCluster][] = [];
            // This is the row above us, we'll scan all immediate neighbors there.
            for (let xP = x - 1; xP < x + 2; xP++) {
                if (xP >= mat.width) continue;

                const prior = mat.get(xP, y - 1);
                if (
                    prior !== undefined &&
                    clustersAreCloseEnough(current, prior, (x - xP) * mat.xStep, mat.yStep, params, acceptDistances)
                ) {
                    const priorLabel = labelReplacements.find(prior.label);
                    if (priorLabel === undefined) continue;
                    const distance = clusterAngleDistance(clusters, current.label, priorLabel, zeroEpsilon);
                    priors.push([distance, prior]);
                }
            }

            // This is the cell just to the left of us.
            {
                const prior = mat.get(x - 1, y);
                if (
                    prior !== undefined &&
                    clustersAreCloseEnough(current, prior, mat.xStep, 0, params, acceptDistances)
                ) {
                    const priorLabel = labelReplacements.find(prior.label);
                    if (priorLabel === undefined) continue;
                    const distance = clusterAngleDistance(clusters, current.label, priorLabel, zeroEpsilon);
                    priors.push([distance, prior]);
                }
            }

            priors.sort((l, r) => l[0] - r[0]);

            const best = priors[0];
            if (best === undefined || best[0] > angleEpsilon) continue;

            mergeClusterChunks(labelReplacements, clusters, best[1].label, current.label, zeroEpsilon);

            priors.shift();

            // It's possible that all of our neighbors are also eligible to be merged
            // into the best cluster; we'll do that here.
            for (let maybeNeighbor = priors.shift(); maybeNeighbor !== undefined; maybeNeighbor = priors.shift()) {
                const neighbor = maybeNeighbor[1];
                // We know that these clusters were close enough already to be
                // considered for the initial merge, so we don't need to check that
                // again.
                const curLabel = labelReplacements.find(current.label);
                const neighborLabel = labelReplacements.find(neighbor.label);

                // If these belong to the same set then there's no need to merge
                // them again.
                if (curLabel === neighborLabel || curLabel === undefined || neighborLabel === undefined) continue;

                const distance = clusterAngleDistance(clusters, curLabel, neighborLabel, zeroEpsilon);
                if (distance <= angleEpsilon) {
                    mergeClusterChunks(labelReplacements, clusters, curLabel, neighborLabel, zeroEpsilon);
                }
            }
        }
    }

    // Now, scan through the chunks we had and group them by cluster.
    const chunksByCluster = new Map<number, FoundChunk[]>();
    for (const chunk of foundChunks) {
        const chunkLabel = labelReplacements.find(chunk.angleCluster.label);
        if (chunkLabel === undefined) continue;

        const priorCluster = chunksByCluster.get(chunkLabel) ?? [];
        priorCluster.push(chunk);
        chunksByCluster.set(chunkLabel, priorCluster);
    }

    const possibleBarcodes: PossibleBarcode[] = [];
    for (const cluster of chunksByCluster.values()) {
        if (cluster.length < minClusterSize) continue;

        const chunkBoxes: BoundingBox[] = [];
        const chunkPoints: [number, number][] = [];

        let theta = 0;

        for (const chunk of cluster) {
            const { angleCluster, xOffset, yOffset } = chunk;
            const { minX, maxX, minY, maxY } = angleCluster;
            const topRight: [number, number] = [maxX + xOffset, maxY + yOffset];
            const topLeft: [number, number] = [minX + xOffset, maxY + yOffset];
            const bottomLeft: [number, number] = [minX + xOffset, minY + yOffset];
            const bottomRight: [number, number] = [maxX + xOffset, minY + yOffset];

            chunkBoxes.push({
                vertex1: topRight,
                vertex2: topLeft,
                vertex3: bottomLeft,
                vertex4: bottomRight,

                width: maxX - minX,
                height: maxY - minY,
            });
            chunkPoints.push(topRight, topLeft, bottomLeft, bottomRight);
            theta += angleCluster.theta > Math.PI - zeroEpsilon ? 0 : angleCluster.theta;
        }

        theta /= cluster.length;
        theta += PI_2;

        if (theta > Math.PI) {
            theta -= Math.PI;
        }

        const clusterBox = computeBoundingBox(chunkPoints, theta);
        const clusterTangents = linesForBoundingBox(clusterBox);
        possibleBarcodes.push({
            boundingBox: clusterBox,
            boundingLines: clusterTangents,
            chunks: chunkBoxes,
        });
    }

    return possibleBarcodes;
}

export type OverlappingBarcodesParameters = Readonly<{
    areaFactor: number;
    maxAngleDelta: number;
}>;

// An important internal feature of this function is that it leaves the barcode
// elements alone if they cannot be merged. This allows consumers to rely on
// identity for mapping purposes: the identity of un-merged barcodes will
// remain the same.
export function mergeOverlappingPossibleBarcodes(
    barcodes: readonly PossibleBarcode[],
    { areaFactor, maxAngleDelta }: OverlappingBarcodesParameters
): PossibleBarcode[] {
    let workingBarcodes = barcodes.slice();

    while (true) {
        let continueWhile = false;
        for (let i = 0; i < workingBarcodes.length; i++) {
            const base = workingBarcodes[i];
            const baseBox = base.boundingBox;
            const baseAngle = directionAngleForBoundingBox(baseBox);
            const baseQuad: Quadrilateral = [baseBox.vertex1, baseBox.vertex2, baseBox.vertex3, baseBox.vertex4];
            const baseArea = areaOfConvexQuadrilateral(baseQuad);
            if (baseArea * areaFactor <= effectiveIntersectionZero) continue;
            let continueFor = true;

            for (let j = i + 1; j < workingBarcodes.length; j++) {
                const other = workingBarcodes[j];
                const otherBox = other.boundingBox;
                const otherAngle = directionAngleForBoundingBox(otherBox);
                if (Math.abs(baseAngle - otherAngle) > maxAngleDelta) continue;

                const otherQuad: Quadrilateral = [
                    otherBox.vertex1,
                    otherBox.vertex2,
                    otherBox.vertex3,
                    otherBox.vertex4,
                ];
                const otherArea = areaOfConvexQuadrilateral(otherQuad);
                if (otherArea * areaFactor <= effectiveIntersectionZero) continue;

                const sharedArea = areaOfConvexQuadrilateralOverlap(baseQuad, otherQuad);

                if (sharedArea < areaFactor * baseArea && sharedArea < areaFactor * otherArea) {
                    continue;
                }

                const boundingBox = computeBoundingBox(baseQuad.concat(otherQuad), (baseAngle + otherAngle) / 2);
                const boundingLines = linesForBoundingBox(boundingBox);

                const replacement = {
                    boundingBox,
                    boundingLines,
                    chunks: base.chunks.concat(other.chunks),
                };

                let newWorkingBarcodes = workingBarcodes.slice(0, i);
                newWorkingBarcodes.push(replacement);
                newWorkingBarcodes = newWorkingBarcodes.concat(
                    workingBarcodes.slice(i + 1, j),
                    workingBarcodes.slice(j + 1)
                );
                workingBarcodes = newWorkingBarcodes;

                continueFor = false;
                continueWhile = true;
                break;
            }

            if (!continueFor) break;
        }
        if (!continueWhile) break;
    }

    return workingBarcodes;
}
